import { api } from "../../../api/apiSlice";

// Technical Site Survey (Batch216, 2026-10-26) - base /technical/surveys.
// Technicians create surveys on the phone and mark them Completed; the office
// (Quotation.Create) reviews them here, may edit a COMPLETED survey, and
// converts it to a quotation through the existing quotation create form,
// after which PUT /{guid}/quotation links the new quotation (COMPLETED -> QUOTED).
//
// Attachments are tagged on the survey itself (the detail carries the list),
// so an upload/delete simply refreshes the detail.
const surveyTags = (recordGuid) => [
  { type: "TechnicalSurvey", id: recordGuid },
  { type: "TechnicalSurvey", id: "LIST" },
];

export const surveysApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Active quotation prototypes of one branch, with their PRODUCT lines only.
    getSurveyServices: builder.query({
      query: (branchId) => ({ url: "/technical/surveys/services", params: { branchId } }),
    }),
    // PagedResult<SurveyListItem> = { items, totalCount, page, pageSize }
    listSurveys: builder.query({
      query: (params) => ({ url: "/technical/surveys", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((s) => ({ type: "TechnicalSurvey", id: s.recordGuid })),
              { type: "TechnicalSurvey", id: "LIST" },
            ]
          : [{ type: "TechnicalSurvey", id: "LIST" }],
    }),
    getSurvey: builder.query({
      query: (recordGuid) => ({ url: `/technical/surveys/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "TechnicalSurvey", id: recordGuid }],
    }),
    createSurvey: builder.mutation({
      query: (body) => ({ url: "/technical/surveys", method: "POST", data: body }),
      invalidatesTags: [{ type: "TechnicalSurvey", id: "LIST" }],
    }),
    // Replaces the header fields AND all lines - always send the full request.
    updateSurvey: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/surveys/${recordGuid}`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => surveyTags(recordGuid),
    }),
    completeSurvey: builder.mutation({
      query: (recordGuid) => ({ url: `/technical/surveys/${recordGuid}/complete`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => surveyTags(recordGuid),
    }),
    cancelSurvey: builder.mutation({
      query: ({ recordGuid, reason }) => ({ url: `/technical/surveys/${recordGuid}/cancel`, method: "POST", data: { reason } }),
      invalidatesTags: (result, error, { recordGuid }) => surveyTags(recordGuid),
    }),
    // COMPLETED -> QUOTED once the quotation made from this survey is saved.
    linkSurveyQuotation: builder.mutation({
      query: ({ recordGuid, quotationRecordGuid }) => ({
        url: `/technical/surveys/${recordGuid}/quotation`,
        method: "PUT",
        data: { quotationRecordGuid },
      }),
      invalidatesTags: (result, error, { recordGuid }) => surveyTags(recordGuid),
    }),

    // ---- Attachments ----
    uploadSurveyAttachment: builder.mutation({
      query: ({ recordGuid, file, kind, caption }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (kind) formData.append("kind", kind);
        if (caption) formData.append("caption", caption);
        return { url: `/technical/surveys/${recordGuid}/attachments`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => surveyTags(recordGuid),
    }),
    // Authenticated bytes, fetched as a Blob (axiosBaseQuery passes responseType
    // through and unwraps the API's error message from a Blob body). Never
    // changes once stored, so it is kept for a while.
    getSurveyAttachmentContent: builder.query({
      query: ({ recordGuid, attachmentGuid }) => ({
        url: `/technical/surveys/${recordGuid}/attachments/${attachmentGuid}/content`,
        responseType: "blob",
      }),
      keepUnusedDataFor: 600,
    }),
    deleteSurveyAttachment: builder.mutation({
      query: ({ recordGuid, attachmentGuid }) => ({
        url: `/technical/surveys/${recordGuid}/attachments/${attachmentGuid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { recordGuid }) => surveyTags(recordGuid),
    }),
  }),
});

export const {
  useGetSurveyServicesQuery,
  useListSurveysQuery,
  useGetSurveyQuery,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useCompleteSurveyMutation,
  useCancelSurveyMutation,
  useLinkSurveyQuotationMutation,
  useUploadSurveyAttachmentMutation,
  useGetSurveyAttachmentContentQuery,
  useLazyGetSurveyAttachmentContentQuery,
  useDeleteSurveyAttachmentMutation,
} = surveysApi;
